import { NotificationItem } from '../types/notifications';
import { ServerNotification, apiService } from '../lib/api';

export class NotificationApiService {
  // Transform server notification to UI notification format
  private transformServerNotification(serverNotification: ServerNotification): NotificationItem {
    const { id, content, type, seen, createdAt, redirectId, sender } = serverNotification;

    // Handle admin notifications specially - they don't have a sender
    if (type === 'adminNotification') {
      // Parse JSON content to extract title and body
      let title = 'Admin Announcement';
      let message = content;

      try {
        const parsed = JSON.parse(content);
        if (parsed.title) title = parsed.title;
        if (parsed.body) message = parsed.body;
      } catch (e) {
        // If not JSON, treat as plain text body
        message = content;
      }

      return {
        id: `server_${id}`,
        type: 'adminNotification',
        title,
        message,
        createdAt: createdAt,
        isRead: seen,
        actionData: {
          postId: redirectId
        },
        actor: {
          id: 0,
          name: 'Admin',
          avatar: undefined
        }
      };
    }

    // Map server types to UI types
    const typeMapping: Record<string, NotificationItem['type']> = {
      'postUpvote': 'upvote',
      'commentUpvote': 'upvote',
      'pollVote': 'poll_vote',
      'comment': 'comment',
      'communityPost': 'community_post'
    };

    // Generate title based on type
    const titleMapping: Record<string, string> = {
      'postUpvote': 'New Upvote!',
      'commentUpvote': 'New Upvote!',
      'pollVote': 'Poll Vote!',
      'comment': 'New Comment!',
      'communityPost': 'New Post!'
    };

    return {
      id: `server_${id}`,
      type: typeMapping[type] || 'upvote',
      title: titleMapping[type] || 'New Notification',
      message: `${sender.username} ${content}`,
      createdAt: createdAt,
      isRead: seen,
      actionData: {
        postId: redirectId,
        userId: sender.id
      },
      actor: {
        id: sender.id,
        name: sender.username,
        avatar: sender.profilePicture || undefined
      }
    };
  }

  // Get notifications from server
  async getNotifications(page: number = 0, limit: number = 50): Promise<NotificationItem[]> {
    try {
      console.log('📡 Fetching server notifications...', { page, limit });
      const serverNotifications = await apiService.getNotifications(page, limit);
      
      console.log('🔄 Transforming server notifications to UI format...');
      const uiNotifications = serverNotifications.map(notification => 
        this.transformServerNotification(notification)
      );

      console.log('✅ Transformed notifications:', uiNotifications.length);
      return uiNotifications;
    } catch (error) {
      console.error('❌ Failed to fetch notifications from server:', error);
      // Return empty array on error to prevent breaking the UI
      return [];
    }
  }

  // Get unread count from server
  async getUnreadCount(): Promise<number> {
    try {
      // Fetch first 100 notifications to count unread
      const notifications = await this.getNotifications(0, 100);
      const unreadCount = notifications.filter(n => !n.isRead).length;
      console.log('🔔 Server unread count:', unreadCount);
      return unreadCount;
    } catch (error) {
      console.error('❌ Failed to get unread count from server:', error);
      return 0;
    }
  }

  // Mark notification as read
  async markAsRead(notificationId: string): Promise<void> {
    try {
      // Extract the actual server ID from the prefixed ID
      const serverId = notificationId.replace('server_', '');
      console.log('📝 Marking notification as read:', serverId);
      
      await apiService.markNotificationAsSeen(serverId);
      console.log('✅ Notification marked as read successfully');
    } catch (error) {
      console.error('❌ Failed to mark notification as read:', error);
      throw error;
    }
  }

  // Mark all notifications as read (future implementation)
  async markAllAsRead(): Promise<void> {
    // TODO: Implement server endpoint for marking all as read
    console.log('📝 Mark all as read (not implemented on server)');
  }
}

export const notificationApiService = new NotificationApiService();